import { logger } from "@/utils/logger";
/**
 * 缩放工具函数 - 处理页面缩放相关的问题
 */

// 缩放事件类型
export interface ZoomEvent {
  scale: number;
  delta: number;
  timestamp: number;
}

// 缩放状态
export interface ZoomState {
  currentScale: number;
  isZooming: boolean;
  lastZoomTime: number;
}

// 全局缩放状态
let zoomState: ZoomState = {
  currentScale: 1,
  isZooming: false,
  lastZoomTime: 0,
};

// 缩放事件监听器
const zoomListeners: ((event: ZoomEvent) => void)[] = [];

/**
 * 获取当前缩放比例
 */
export function getCurrentZoom(): number {
  return zoomState.currentScale;
}

/**
 * 检测是否正在缩放
 */
export function isZooming(): boolean {
  return zoomState.isZooming;
}

/**
 * 添加缩放事件监听器
 */
export function addZoomListener(listener: (event: ZoomEvent) => void): void {
  zoomListeners.push(listener);
}

/**
 * 移除缩放事件监听器
 */
export function removeZoomListener(listener: (event: ZoomEvent) => void): void {
  const index = zoomListeners.indexOf(listener);
  if (index > -1) {
    zoomListeners.splice(index, 1);
  }
}

/**
 * 触发缩放事件
 */
function triggerZoomEvent(scale: number, delta: number): void {
  const event: ZoomEvent = {
    scale,
    delta,
    timestamp: Date.now(),
  };

  zoomState.currentScale = scale;
  zoomState.isZooming = true;
  zoomState.lastZoomTime = event.timestamp;

  // 通知所有监听器
  zoomListeners.forEach((listener) => {
    try {
      listener(event);
    } catch (error) {
      logger.error("缩放事件监听器执行失败:", error);
    }
  });

  // 延迟重置缩放状态
  setTimeout(() => {
    zoomState.isZooming = false;
  }, 300);
}

/**
 * 检测缩放变化
 */
function detectZoomChange(): void {
  const currentScale = getZoomScale();
  const delta = currentScale - zoomState.currentScale;

  if (Math.abs(delta) > 0.01) {
    // 缩放变化超过1%
    triggerZoomEvent(currentScale, delta);
  }
}

/**
 * 获取浏览器缩放比例
 */
function getZoomScale(): number {
  // 方法1: 使用window.devicePixelRatio
  if (window.devicePixelRatio) {
    return window.devicePixelRatio;
  }

  // 方法2: 使用screen.width和window.innerWidth
  const screenWidth = screen.width;
  const windowWidth = window.innerWidth;

  if (screenWidth && windowWidth) {
    return screenWidth / windowWidth;
  }

  // 方法3: 使用document.documentElement.clientWidth
  const documentWidth = document.documentElement.clientWidth;
  if (documentWidth && windowWidth) {
    return windowWidth / documentWidth;
  }

  // 默认返回1
  return 1;
}

/**
 * 修复缩放时的布局问题
 */
export function fixZoomLayout(): void {
  // 移除旧版布局修复留下的内联样式，避免影响新的响应式布局
  const cleanupSelectors = [
    ".main-layout",
    ".layout-main",
    ".layout-content",
    ".pcp-multi-container",
    ".main-content",
  ];

  cleanupSelectors.forEach((selector) => {
    document.querySelectorAll<HTMLElement>(selector).forEach((element) => {
      element.style.removeProperty("min-height");
      element.style.removeProperty("min-width");
      element.style.removeProperty("position");
      element.style.removeProperty("overflow");
      element.style.removeProperty("display");
      element.style.removeProperty("flex-direction");
      element.style.removeProperty("grid-template-columns");
      element.style.removeProperty("grid-template-areas");
      element.style.removeProperty("grid-area");
    });
  });

  document.body.style.removeProperty("transform");
}

/**
 * 初始化缩放检测
 */
export function initZoomDetection(): void {
  // 监听窗口大小变化
  window.addEventListener("resize", () => {
    detectZoomChange();
    fixZoomLayout();
  });

  // 监听页面可见性变化
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      detectZoomChange();
      fixZoomLayout();
    }
  });

  // 监听页面加载完成
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      detectZoomChange();
      fixZoomLayout();
    });
  } else {
    detectZoomChange();
    fixZoomLayout();
  }

  // 定期检测缩放变化
  setInterval(() => {
    detectZoomChange();
  }, 1000);
}

/**
 * 重置缩放状态
 */
export function resetZoomState(): void {
  zoomState = {
    currentScale: 1,
    isZooming: false,
    lastZoomTime: 0,
  };
}

/**
 * 获取缩放信息
 */
export function getZoomInfo(): ZoomState {
  return { ...zoomState };
}

/**
 * 强制修复所有缩放问题
 */
export function forceFixZoom(): void {
  // 重置缩放状态
  resetZoomState();

  // 强制重绘整个页面
  document.body.style.display = "none";
  document.body.offsetHeight; // 触发重排
  document.body.style.display = "";

  // 应用布局修复
  fixZoomLayout();

  // 重新检测缩放
  detectZoomChange();
}
